import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "wouter";
import { Building2, ExternalLink, Clock, Mail, Flag, LogIn, ShieldCheck, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { JobPost } from "@workspace/api-client-react";
import { useGetMe, getGetMeQueryKey, useReportJob } from "@workspace/api-client-react";
import { isActionableReferral } from "@/lib/job-referral";

interface JobCardProps {
  job: JobPost;
  onNavigate?: () => void;
}

const WORK_MODE_LABELS: Record<string, string> = {
  remote: "Remote",
  hybrid: "Hybrid",
  "on-site": "On-site",
};

const WORK_MODE_COLORS: Record<string, string> = {
  remote: "bg-emerald-900/40 border-emerald-700/40 text-emerald-300",
  hybrid: "bg-amber-900/40 border-amber-700/40 text-amber-300",
  "on-site": "bg-zinc-800/60 border-zinc-700/40 text-zinc-300",
};

export default function JobCard({ job, onNavigate }: JobCardProps) {
  const [, navigate] = useLocation();
  const { data: me } = useGetMe({ query: { queryKey: getGetMeQueryKey(), retry: false } });
  const { toast } = useToast();

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reported, setReported] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  const reportMutation = useReportJob({
    mutation: {
      onSuccess: () => {
        setReported(true);
        setReportOpen(false);
        setReportReason("");
        toast({ title: "Report submitted", description: "Thank you. Our team will review this listing." });
      },
      onError: (err: unknown) => {
        const status = (err as { status?: number })?.status;
        if (status === 409) {
          toast({ title: "Already reported", description: "You have already reported this listing.", variant: "destructive" });
          setReported(true);
          setReportOpen(false);
        } else if (status === 401) {
          toast({ title: "Sign in required", description: "Please log in to report a listing.", variant: "destructive" });
        } else {
          toast({ title: "Error", description: "Could not submit report. Please try again.", variant: "destructive" });
        }
      },
    },
  });

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (reportMutation.isPending || reported) return;
    reportMutation.mutate({ id: job.id, data: { reason: reportReason.trim() || undefined } });
  };

  const isBlurred = job.jobTitle == null;

  const workModes: string[] = job.workModes ?? [];
  const locations: string[] = job.locations ?? [];

  const locationLine = (() => {
    if (workModes.includes("remote") && locations.length === 0) return "Remote / Anywhere";
    if (locations.length > 0 && workModes.length === 0) return locations.join(" · ");
    if (locations.length > 0) return locations.join(" · ");
    return null;
  })();

  const renderCTA = () => {
    if (isBlurred) {
      return (
        <div className="flex-1 inline-flex items-center justify-center gap-2 text-xs font-semibold font-sans" style={{ color: "#9B1230" }}>
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
          Verify your alumni status to unlock this listing
        </div>
      );
    }

    if (!me) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); navigate("/login"); }}
          className="flex-1 inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-primary hover:bg-primary/90 text-white text-xs font-bold uppercase tracking-wide transition-colors cursor-pointer"
        >
          <LogIn className="w-3 h-3" />
          Sign In to Apply
        </button>
      );
    }

    if (!me.hasVerifiedRole && !me.verified) {
      return (
        <a
          href="/dashboard"
          onClick={(e) => e.stopPropagation()}
          className="flex-1 inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-xs font-semibold transition-colors border border-white/10"
        >
          <Clock className="w-3 h-3" />
          Pending Verification
        </a>
      );
    }

    const hasLink = job.applicationLink && job.applicationLink.trim().length > 0;
    const hasEmail = job.showEmail && job.recruiterEmail;

    return (
      <>
        {hasLink && (
          <a
            href={job.applicationLink!.startsWith("http") ? job.applicationLink! : `https://${job.applicationLink}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-primary hover:bg-primary/90 text-white text-xs font-bold uppercase tracking-wide transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Apply Now
          </a>
        )}
        {hasEmail && (
          <a
            href={`mailto:${job.recruiterEmail}`}
            onClick={(e) => e.stopPropagation()}
            className={`${hasLink ? "" : "flex-1 "}inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold transition-colors border border-white/10`}
          >
            <Mail className="w-3 h-3" />
            Email Recruiter
          </a>
        )}
      </>
    );
  };

  return (
    <motion.div
      className="bg-zinc-900/60 border border-white/10 hover:border-primary/40 transition-colors duration-300 rounded-xl p-6 flex flex-col relative overflow-hidden group hover:shadow-[0_0_30px_-5px_rgba(220,20,60,0.12)] cursor-pointer"
      onClick={() => { onNavigate?.(); navigate(`/jobs/${job.id}`); }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-zinc-800 border border-white/5 flex items-center justify-center shrink-0 overflow-hidden">
          {job.linkThumbnailUrl && !thumbnailError ? (
            <img
              src={job.linkThumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setThumbnailError(true)}
            />
          ) : (
            <Building2 className="w-5 h-5 text-zinc-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {job.jobTitle != null || job.companyName != null ? (
            <>
              <h3 className="font-bold text-white truncate group-hover:text-primary transition-colors">
                {job.jobTitle || job.companyName}
              </h3>
              {job.jobTitle && (
                <p className="text-xs text-zinc-400 font-sans mt-0.5 truncate">{job.companyName}</p>
              )}
            </>
          ) : (
            <>
              <div className="h-4 w-36 rounded bg-zinc-700 blur-sm select-none pointer-events-none mb-1" />
              <div className="h-3 w-24 rounded bg-zinc-800 blur-sm select-none pointer-events-none mt-0.5" />
            </>
          )}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <p className="text-xs text-zinc-500 font-sans">{timeAgo(job.createdAt)}</p>
            {job.authorId != null && me?.hasVerifiedRole === true && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-cyan-400 border border-cyan-500/20 bg-cyan-500/10 leading-none font-sans">
                <ShieldCheck className="w-2.5 h-2.5 shrink-0" />
                Shared by Member
              </span>
            )}
            {isActionableReferral(job) && me?.hasVerifiedRole === true && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-emerald-400 border border-emerald-500/20 bg-emerald-500/10 leading-none font-sans">
                Referral Available
              </span>
            )}
          </div>
        </div>
        {me?.profileId != null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!reported) setReportOpen((o) => !o);
            }}
            className={`shrink-0 p-1.5 rounded transition-colors ${
              reported
                ? "text-amber-500 cursor-default"
                : "text-zinc-600 hover:text-zinc-400"
            }`}
            title={reported ? "Already reported" : "Report this listing"}
            aria-label={reported ? "Already reported" : "Report this listing"}
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {reportOpen && !reported && (
        <div
          className="mb-4 p-3 rounded-lg bg-zinc-800/80 border border-white/10 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wide">Report this listing</p>
          <Textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Optional: describe the issue..."
            className="bg-zinc-900 border-white/10 text-white text-xs font-sans resize-none h-16 placeholder:text-zinc-600"
            maxLength={500}
          />
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="text-zinc-400 hover:text-white text-xs h-7"
              onClick={(e) => { e.stopPropagation(); setReportOpen(false); setReportReason(""); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={reportMutation.isPending}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold h-7"
              onClick={handleReport}
            >
              {reportMutation.isPending ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-3 font-sans mb-4">
        <div>
          <span className="text-zinc-500 text-xs uppercase tracking-wider block mb-1 font-semibold">
            Disciplines / Titles
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(job.industryFocus ?? []).map((f, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="bg-zinc-800/60 text-zinc-300 border border-white/5 font-sans rounded text-xs px-2 py-0.5"
              >
                {f}
              </Badge>
            ))}
          </div>
        </div>

        {(workModes.length > 0 || locationLine) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {workModes.map((m) => (
              <span
                key={m}
                className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border font-sans ${WORK_MODE_COLORS[m] ?? "bg-zinc-800/60 border-zinc-700/40 text-zinc-300"}`}
              >
                {WORK_MODE_LABELS[m] ?? m}
              </span>
            ))}
            {locationLine && (
              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 font-sans">
                <MapPin className="w-3 h-3 shrink-0" />
                {locationLine}
              </span>
            )}
          </div>
        )}

        <div>
          <span className="text-zinc-500 text-xs uppercase tracking-wider block mb-1 font-semibold">
            Role Description
          </span>
          {job.specificNeeds == null ? (
            <div aria-hidden className="select-none pointer-events-none space-y-1.5">
              <div className="h-3 w-full rounded bg-zinc-700/60" />
              <div className="h-3 w-5/6 rounded bg-zinc-700/60" />
              <div className="h-3 w-4/6 rounded bg-zinc-700/60" />
            </div>
          ) : (
            <p className="text-zinc-300 text-sm leading-relaxed line-clamp-3">{job.specificNeeds}</p>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-white/5 flex items-center gap-2">
        {renderCTA()}
      </div>
    </motion.div>
  );
}
